import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState, StatusPill } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { PatientPicker } from "@/components/PatientPicker";
import { PrintPreview, PrescriptionDoc, HospitalInvoiceDoc, useActiveTemplate } from "@/components/PrintDoc";
import { useAuth } from "@/context/AuthContext";
import { inr, dmyTime, today } from "@/lib/format";
import { runSingleFlight } from "@/lib/singleFlight";

const EMPTY_RX_LINE = { name: "", strength: "", dosage: "", frequency: "", duration: "", instructions: "" };
const OPD_SUBMISSION_STORAGE_KEY = "hospital-opd-submission-key";

export default function Opd() {
  const { can, user } = useAuth();
  const rxTpl = useActiveTemplate("hospital", "opd_prescription");
  const [items, setItems] = useState(null);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ patient_id: "", doctor_id: "", symptoms: "", consultation_fee: "", visit_type: "new", collection_mode: "due", payment_method: "cash", payment_reference: "", initial_payments: [], services: [], service_description: "", service_rate: "", discount: "", discount_type: "amount", tax_percent: "" });
  const [picked, setPicked] = useState(null);
  const [formError, setFormError] = useState("");
  const [consult, setConsult] = useState(null);
  const [labTests, setLabTests] = useState([]);
  const [cForm, setCForm] = useState({ diagnosis: "", treatment: "", notes: "", follow_up_date: "",
    advice: "", medicines: [{ ...EMPTY_RX_LINE }], lab_test_ids: [] });
  const [rxPreview, setRxPreview] = useState(null);
  const [dayFilter, setDayFilter] = useState(today());
  const [prescriptionMode, setPrescriptionMode] = useState("system");
  const [moduleGst, setModuleGst] = useState(0);
  const [billPreview, setBillPreview] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [pendingRx, setPendingRx] = useState(null);
  const [cleanupFor, setCleanupFor] = useState(null);
  const [cleanupReason, setCleanupReason] = useState("");
  const [cleanupConfirmed, setCleanupConfirmed] = useState(false);
  const [cleanupError, setCleanupError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submissionLock = useRef(false);
  const submissionKey = useRef(null);
  const submissionAttempted = useRef(false);
  const ensureSubmissionKey = () => {
    if (!submissionKey.current) {
      submissionKey.current = window.sessionStorage.getItem(OPD_SUBMISSION_STORAGE_KEY)
        || window.crypto.randomUUID();
      window.sessionStorage.setItem(OPD_SUBMISSION_STORAGE_KEY, submissionKey.current);
    }
  };
  const clearSubmissionKey = () => {
    submissionKey.current = null;
    window.sessionStorage.removeItem(OPD_SUBMISSION_STORAGE_KEY);
  };

  const load = useCallback(async () => {
    setItems(null);
    try {
      const { data } = await api.get("/hospital/opd", { params: { day: dayFilter || undefined } });
      setItems(data.items);
    } catch (e) { setError(apiError(e)); }
  }, [dayFilter]);

  useEffect(() => {
    if (!can("hospital.opd.manage")) return;
    load();
    Promise.all([api.get("/hospital/patients", { params: { limit: 200 } }), api.get("/hospital/doctors")])
      .then(([p, d]) => { setPatients(p.data.items); setDoctors(d.data.items); }).catch(() => {});
    if (can("lab.orders.create")) {
      api.get("/lab/tests", { params: { active: true } })
        .then(({ data }) => setLabTests(data.items || [])).catch(() => {});
    }
    api.get("/settings/full").then(({ data }) => {
      setPrescriptionMode(data.business?.general?.opd_prescription_mode || "system");
      setModuleGst(Number(data.business?.module_gst?.hospital?.percent || 0));
    }).catch(() => {});
  }, [load, can]);

  if (!can("hospital.opd.manage")) return <NoAccess what="the OPD queue" />;

  const canCleanup = can("hospital.records.cleanup")
    && ["business_admin", "hospital_admin"].includes(user?.role);

  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const opdLines = () => {
    const lines = [{ description: `Consultation — Dr. ${(doctors.find((d) => d.doctor_id === form.doctor_id) || {}).name || ""}`,
      quantity: 1, rate: Number(form.consultation_fee || 0), discount: 0 }];
    if (form.service_description.trim()) lines.push({ description: form.service_description, quantity: 1, rate: Number(form.service_rate || 0), discount: 0 });
    return lines;
  };
  const calcBill = (lines) => {
    let subtotal = 0, lineTax = 0;
    lines.forEach((l) => { const taxable = Math.max(l.quantity * l.rate - (l.discount || 0), 0); subtotal += taxable; lineTax += taxable * moduleGst / 100; });
    subtotal = round2(subtotal);
    const billTaxPercent = form.tax_percent === "" ? 0 : Number(form.tax_percent);
    const tax = round2(lineTax + subtotal * billTaxPercent / 100);
    const invoiceValue = round2(subtotal + tax);
    const disc = Number(form.discount || 0);
    const discountAmount = form.discount_type === "percent" ? round2(invoiceValue * disc / 100) : round2(disc);
    const total = round2(Math.max(invoiceValue - discountAmount, 0));
    const paid = form.collection_mode === "full" ? total
      : form.collection_mode === "split" ? round2(form.initial_payments.reduce((s, p) => s + Number(p.amount || 0), 0)) : 0;
    return { subtotal, tax, discountAmount, total, paid, due: round2(Math.max(total - paid, 0)) };
  };

  const openPreview = (e) => {
    e.preventDefault(); setFormError("");
    if (submitting || submissionAttempted.current) return;
    if (!form.doctor_id) { setFormError("Select a doctor."); return; }
    const discount = Number(form.discount || 0);
    if (form.discount_type === "percent" && discount > 100) {
      setFormError("Percentage discount cannot exceed 100%."); return;
    }
    setBillPreview(true);
  };

  const confirmSave = async () => {
    ensureSubmissionKey();
    return runSingleFlight(submissionLock,
      () => { submissionAttempted.current = true; setSubmitting(true); },
      async () => {
        setFormError("");
        try {
          const { data } = await api.post("/hospital/opd", {
            ...form,
            idempotency_key: submissionKey.current,
            consultation_fee: Number(form.consultation_fee || 0),
            discount: Number(form.discount || 0),
            tax_percent: form.tax_percent === "" ? null : Number(form.tax_percent),
            services: form.service_description.trim() ? [{ description: form.service_description, quantity: 1, rate: Number(form.service_rate || 0) }] : [],
          });
          toast.success(`✓ OPD registration successful — Token ${data.visit.token_no} · ${data.visit.patient_name}`);
          // Reception hands a prescription to the patient — no consultation form is opened here.
          let rx = null;
          try {
            const { data: pr } = await api.post("/hospital/prescriptions", {
              patient_id: data.visit.patient_id, doctor_id: data.visit.doctor_id,
              visit_id: data.visit.visit_id, prescription_mode: "blank", medicines: [] });
            rx = { rx: { ...pr.prescription, encounter_type: "opd", token_no: data.visit.token_no }, business: pr.business };
          } catch { /* prescription is optional */ }
          try {
            const { data: inv } = await api.get(`/hospital/invoices/${data.invoice.invoice_id}`);
            setReceipt({ invoice: inv.invoice, patient: inv.patient, business: inv.business });
          } catch { setReceipt({ invoice: data.invoice, patient: picked, business: null }); }
          setPendingRx(rx);
          setBillPreview(false); setOpen(false); setPicked(null);
          setForm({ patient_id: "", doctor_id: "", symptoms: "", consultation_fee: "", visit_type: "new", collection_mode: "due", payment_method: "cash", payment_reference: "", initial_payments: [], services: [], service_description: "", service_rate: "", discount: "", discount_type: "amount", tax_percent: "" });
          setCForm({ diagnosis: "", treatment: "", notes: "", follow_up_date: "", advice: "", medicines: [{ ...EMPTY_RX_LINE }], lab_test_ids: [] });
          load();
          clearSubmissionKey();
          submissionAttempted.current = false;
        } catch (err) {
          setFormError(apiError(err));
          const detail = err.response?.data?.detail || "";
          const keyConflict = err.response?.status === 409
            && detail.includes("different registration details");
          if (err.response && err.response.status < 500
              && (err.response.status !== 409 || keyConflict)) {
            clearSubmissionKey();
            submissionAttempted.current = false;
          }
        }
      },
      () => setSubmitting(false));
  };

  const closeRegistration = () => {
    if (submitting || submissionAttempted.current) return;
    clearSubmissionKey();
    setOpen(false);
  };

  const submitCleanup = async (event) => {
    event.preventDefault();
    setCleanupError("");
    if (!cleanupConfirmed) {
      setCleanupError("Confirm permanent deletion to continue."); return;
    }
    try {
      await api.post(`/hospital/opd/${cleanupFor.visit_id}/cleanup`, {
        confirmation: true,
        reason: cleanupReason.trim(),
      });
      toast.success("Empty OPD registration permanently removed and audited.");
      setCleanupFor(null); setCleanupReason(""); setCleanupConfirmed(false); load();
    } catch (requestError) {
      setCleanupError(apiError(requestError));
    }
  };

  const saveConsult = async (e) => {
    e.preventDefault();
    const visit = consult;
    const medicines = cForm.medicines.filter((m) => m.name.trim());
    try {
      const { data: cRes } = await api.post(`/hospital/opd/${visit.visit_id}/consult`, {
        diagnosis: cForm.diagnosis, treatment: cForm.treatment, notes: cForm.notes,
        follow_up_date: cForm.follow_up_date, medicines: [], lab_test_ids: cForm.lab_test_ids });
      let created = null;
      if (medicines.length) {
        const { data } = await api.post("/hospital/prescriptions", {
          patient_id: visit.patient_id, doctor_id: visit.doctor_id, visit_id: visit.visit_id,
          diagnosis: cForm.diagnosis, advice: cForm.advice,
          follow_up_date: cForm.follow_up_date, medicines });
        created = { rx: { ...data.prescription, encounter_type: "opd" }, business: data.business };
      }
      toast.success("Consultation saved" + (medicines.length ? " with prescription." : ".")
        + (cRes.lab_order ? ` Lab order ${cRes.lab_order.order_no} created (pending billing).` : ""));
      setConsult(null);
      setCForm({ diagnosis: "", treatment: "", notes: "", follow_up_date: "", advice: "", medicines: [{ ...EMPTY_RX_LINE }], lab_test_ids: [] });
      if (created) setRxPreview(created);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="opd-page">
      <PageHeader overline="Hospital" title="OPD queue"
        subtitle="Walk-in or appointment based registration. Every token receives an invoice automatically, with payment collected inline when selected."
        actions={<button onClick={() => {
          ensureSubmissionKey();
          setOpen(true);
        }} className={primaryBtnCls} data-testid="add-opd-btn">OPD registration</button>} />

      <div className="flex flex-wrap gap-3 mb-6">
        <input type="date" value={dayFilter} onChange={(e) => setDayFilter(e.target.value)}
          data-testid="opd-day-filter" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
        <button onClick={() => setDayFilter("")} className={ghostBtnCls} data-testid="opd-all-days-btn">All days</button>
      </div>

      {error && <ErrorState message={error} />}
      {!items ? <Loading /> : (
        <Table testid="opd-table" columns={["Token", "Patient", "Doctor", "Symptoms", "Fee", "Registered", "Status", "Actions"]}
          rows={items} empty={<EmptyState title="No OPD registrations" subtitle="Register a walk-in patient to create a token." testid="opd-empty" />}
          renderRow={(v) => (
            <tr key={v.visit_id} data-testid={`opd-row-${v.visit_id}`}>
              <Td className="font-mono-num text-slate-900 font-semibold">#{v.token_no}</Td>
              <Td className="font-medium text-slate-900">{v.patient_name}<span className="block text-xs text-slate-400">{v.patient_uhid}</span></Td>
              <Td className="text-slate-600">Dr. {v.doctor_name}</Td>
              <Td className="text-slate-600 max-w-[200px] truncate">{v.symptoms || "—"}</Td>
              <Td className="text-slate-800">{inr(v.consultation_fee)}</Td>
              <Td className="text-slate-500 whitespace-nowrap">{dmyTime(v.created_at)}</Td>
              <Td><StatusPill status={v.status} /></Td>
              <Td>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => setConsult(v)} className={ghostBtnCls} data-testid={`consult-${v.visit_id}`}>Consult</button>
                  {canCleanup && (
                    <button onClick={() => { setCleanupFor(v); setCleanupReason(""); setCleanupConfirmed(false); setCleanupError(""); }}
                      className="text-xs font-medium text-red-600 hover:text-red-800"
                      data-testid={`cleanup-opd-${v.visit_id}`}>
                      Remove test record
                    </button>
                  )}
                </div>
              </Td>
            </tr>
          )} />
      )}

      <Modal open={open} onClose={closeRegistration} wide title="OPD registration"
        subtitle="Choose a new or existing patient, then assign a doctor and issue a token." testid="add-opd-modal">
        <div className="mb-6"><PatientPicker selected={picked} onSelect={(p) => { setPicked(p); setForm({ ...form, patient_id: p?.patient_id || "" }); }} /></div>
        {picked && (
        <form onSubmit={openPreview} className="grid gap-5 sm:grid-cols-2">
          <Field label="Visit type">
            <select className={inputCls} value={form.visit_type} data-testid="opd-visit-type-select"
              onChange={(e) => {
                const d = doctors.find((x) => x.doctor_id === form.doctor_id);
                const fee = e.target.value === "follow_up" ? d?.follow_up_fee : e.target.value === "emergency" ? d?.emergency_fee : d?.consultation_fee;
                setForm({ ...form, visit_type: e.target.value, consultation_fee: fee ?? form.consultation_fee });
              }}>
              <option value="new">New consultation</option>
              <option value="follow_up">Follow-up</option>
              <option value="emergency">Emergency</option>
            </select>
          </Field>
          <Field label="Doctor *">
            <select required className={inputCls} value={form.doctor_id} data-testid="opd-doctor-select"
              onChange={(e) => {
                const d = doctors.find((x) => x.doctor_id === e.target.value);
                const fee = form.visit_type === "follow_up" ? d?.follow_up_fee : form.visit_type === "emergency" ? d?.emergency_fee : d?.consultation_fee;
                setForm({ ...form, doctor_id: e.target.value, consultation_fee: fee ?? "" });
              }}>
              <option value="">Select doctor</option>
              {doctors.map((d) => <option key={d.doctor_id} value={d.doctor_id}>Dr. {d.name}</option>)}
            </select>
          </Field>
          <Field label="Consultation fee (₹)"><input type="number" data-testid="opd-fee-input" className={inputCls} value={form.consultation_fee} onChange={(e) => setForm({ ...form, consultation_fee: e.target.value })} /></Field>
          <Field label="GST / tax %"><input type="number" min="0" max="100" step="0.01" data-testid="opd-tax-input" className={inputCls} value={form.tax_percent} onChange={(e) => setForm({ ...form, tax_percent: e.target.value })} /></Field>
          <Field label="Additional service"><input className={inputCls} data-testid="opd-service-description" placeholder="Optional service" value={form.service_description} onChange={(e) => setForm({ ...form, service_description: e.target.value })} /></Field>
          <Field label="Service charge (₹)"><input type="number" min="0" data-testid="opd-service-rate" className={inputCls} value={form.service_rate} onChange={(e) => setForm({ ...form, service_rate: e.target.value })} /></Field>
          <Field label="Discount"><input type="number" min="0" step="0.01" max={form.discount_type === "percent" ? 100 : undefined} data-testid="opd-discount-input" className={inputCls} value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} /></Field>
          <Field label="Discount type"><select className={inputCls} data-testid="opd-discount-type" value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })}><option value="amount">Amount (₹)</option><option value="percent">Percentage (%)</option></select></Field>
          <Field label="Payment in OPD">
            <select className={inputCls} data-testid="opd-collection-mode" value={form.collection_mode}
              onChange={(e) => setForm({ ...form, collection_mode: e.target.value })}>
              <option value="due">Leave due</option><option value="full">Full payment now</option><option value="split">Split payment now</option>
            </select>
          </Field>
          {form.collection_mode === "full" && <div className="sm:col-span-2 grid gap-3 sm:grid-cols-2">
            <select className={inputCls} data-testid="opd-payment-mode" value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>{["cash","upi","card","bank","insurance","cheque","online","other"].map((m) => <option key={m}>{m}</option>)}</select>
            <input className={inputCls} data-testid="opd-payment-reference" placeholder="Reference no. (optional)" value={form.payment_reference} onChange={(e) => setForm({ ...form, payment_reference: e.target.value })} />
          </div>}
          {form.collection_mode === "split" && <div className="sm:col-span-2 space-y-3" data-testid="opd-split-payment-section">
            {form.initial_payments.map((p, i) => <div className="grid gap-3 sm:grid-cols-3" key={i}>
              <input type="number" min="0.01" step="0.01" className={inputCls} placeholder="Amount" value={p.amount} onChange={(e) => setForm({ ...form, initial_payments: form.initial_payments.map((x, n) => n === i ? { ...x, amount: e.target.value } : x) })} />
              <select className={inputCls} value={p.mode} onChange={(e) => setForm({ ...form, initial_payments: form.initial_payments.map((x, n) => n === i ? { ...x, mode: e.target.value } : x) })}>{["cash","upi","card","bank","insurance","cheque","online","other"].map((m) => <option key={m}>{m}</option>)}</select>
              <input className={inputCls} placeholder="Reference" value={p.reference_no} onChange={(e) => setForm({ ...form, initial_payments: form.initial_payments.map((x, n) => n === i ? { ...x, reference_no: e.target.value } : x) })} />
            </div>)}
            <button type="button" className={ghostBtnCls} data-testid="opd-add-split" onClick={() => setForm({ ...form, initial_payments: [...form.initial_payments, { amount: "", mode: form.initial_payments.length ? "upi" : "cash", reference_no: "" }] })}>Add payment component</button>
            <p className="text-xs text-slate-600" data-testid="opd-split-total">Allocated: ₹{form.initial_payments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0)} / ₹{form.consultation_fee || 0}</p>
          </div>}
          <Field label="Symptoms" className="sm:col-span-2"><textarea rows={2} className={inputCls} value={form.symptoms} onChange={(e) => setForm({ ...form, symptoms: e.target.value })} /></Field>
          {formError && <p className="sm:col-span-2 text-sm text-red-600" data-testid="opd-form-error">{formError}</p>}
          <div className="sm:col-span-2"><button className={primaryBtnCls} data-testid="opd-submit-btn">Preview bill</button></div>
        </form>
        )}
      </Modal>

      <Modal open={Boolean(cleanupFor)} onClose={() => setCleanupFor(null)}
        title="Remove empty test OPD record"
        subtitle="This action is available only when the registration has no protected history."
        testid="opd-cleanup-modal">
        <form onSubmit={submitCleanup} className="space-y-5">
          <p className="text-sm text-slate-700" data-testid="opd-cleanup-warning">
            This record contains no financial or clinical transactions and will be permanently deleted.
          </p>
          <Field label="Deletion reason *">
            <textarea required minLength={3} rows={3} className={inputCls}
              data-testid="opd-cleanup-reason" value={cleanupReason}
              onChange={(event) => setCleanupReason(event.target.value)} />
          </Field>
          <label className="flex items-start gap-2 text-sm text-slate-700" data-testid="opd-cleanup-confirmation">
            <input type="checkbox" checked={cleanupConfirmed}
              onChange={(event) => setCleanupConfirmed(event.target.checked)} />
            I understand this cannot be undone.
          </label>
          {cleanupError && <p className="text-sm font-medium text-red-600" data-testid="opd-cleanup-error">{cleanupError}</p>}
          <button className={primaryBtnCls} data-testid="opd-cleanup-submit">Permanently delete</button>
        </form>
      </Modal>

      <Modal open={Boolean(consult)} onClose={() => setConsult(null)} wide title="Consultation & prescription"
        subtitle={consult ? `${consult.patient_name} · Token ${consult.token_no} · Dr. ${consult.doctor_name}` : ""}
        testid="consult-modal">
        <form onSubmit={saveConsult} className="space-y-6">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Diagnosis"><input className={inputCls} data-testid="consult-diagnosis-input" value={cForm.diagnosis} onChange={(e) => setCForm({ ...cForm, diagnosis: e.target.value })} /></Field>
            <Field label="Follow-up date"><input type="date" className={inputCls} value={cForm.follow_up_date} onChange={(e) => setCForm({ ...cForm, follow_up_date: e.target.value })} /></Field>
            <Field label="Treatment" className="sm:col-span-2"><textarea rows={2} className={inputCls} value={cForm.treatment} onChange={(e) => setCForm({ ...cForm, treatment: e.target.value })} /></Field>
          </div>
          <div className="space-y-3">
            <p className="overline text-[#EA580C]">Prescription</p>
            {cForm.medicines.map((m, idx) => (
              <div key={idx} className="grid gap-3 sm:grid-cols-6" data-testid={`consult-rx-line-${idx}`}>
                {[["name", "Medicine (e.g. Paracetamol)"], ["strength", "Strength (e.g. 500 mg)"], ["dosage", "Dosage (e.g. 1 tab)"],
                  ["frequency", "Frequency (e.g. 1-0-1)"], ["duration", "Duration (e.g. 5 days)"], ["instructions", "Instructions (e.g. after food)"]].map(([k, ph]) => (
                  <input key={k} placeholder={ph} className={inputCls} data-testid={`consult-rx-${k}-${idx}`} value={m[k]}
                    onChange={(e) => setCForm({ ...cForm, medicines: cForm.medicines.map((x, i) => i === idx ? { ...x, [k]: e.target.value } : x) })} />
                ))}
              </div>
            ))}
            <button type="button" onClick={() => setCForm({ ...cForm, medicines: [...cForm.medicines, { ...EMPTY_RX_LINE }] })}
              className={ghostBtnCls} data-testid="consult-add-medicine-btn">Add medicine</button>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Advice / instructions"><textarea rows={2} className={inputCls} data-testid="consult-advice-input" value={cForm.advice} onChange={(e) => setCForm({ ...cForm, advice: e.target.value })} /></Field>
            <Field label="Notes"><textarea rows={2} className={inputCls} value={cForm.notes} onChange={(e) => setCForm({ ...cForm, notes: e.target.value })} /></Field>
          </div>
          {can("lab.orders.create") && labTests.length > 0 && (
            <div className="space-y-3" data-testid="consult-lab-section">
              <p className="overline text-[#EA580C]">Order lab tests</p>
              <p className="text-xs text-slate-500">Selected tests create a lab order in <strong>pending billing</strong>. Front desk creates the invoice and collects payment before sample collection.</p>
              <div className="border border-slate-200 rounded-lg divide-y max-h-44 overflow-auto">
                {labTests.map((t) => (
                  <label key={t.test_id} className="flex items-center justify-between px-3 py-2 text-sm cursor-pointer hover:bg-slate-50" data-testid={`consult-lab-option-${t.test_id}`}>
                    <span className="flex items-center gap-2">
                      <input type="checkbox" checked={cForm.lab_test_ids.includes(t.test_id)}
                        onChange={(e) => setCForm({ ...cForm, lab_test_ids: e.target.checked
                          ? [...cForm.lab_test_ids, t.test_id]
                          : cForm.lab_test_ids.filter((x) => x !== t.test_id) })} />
                      {t.name} <span className="text-slate-400">· {t.category || "—"}</span>
                    </span>
                    <span className="text-slate-600">₹{t.price}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <button className={primaryBtnCls} data-testid="consult-submit-btn">Save consultation &amp; preview prescription</button>
        </form>
      </Modal>

      <PrintPreview open={Boolean(rxPreview)} onClose={() => setRxPreview(null)} paperSize={rxTpl?.paper_size || "A4"}
        template={rxTpl} title="Prescription" subtitle={rxPreview?.rx?.patient_name} prescriptionPreview
        testid="opd-rx-print-preview">
        <PrescriptionDoc rx={rxPreview?.rx} business={rxPreview?.business} template={rxTpl} />
      </PrintPreview>

      <Modal open={billPreview} onClose={() => {
        if (!submitting && !submissionAttempted.current) setBillPreview(false);
      }} title="Preview OPD bill"
        subtitle="Review the bill before saving. No invoice is created until you Save & Print."
        testid="opd-bill-preview-modal">
        {billPreview && (() => {
          const b = calcBill(opdLines());
          const dr = doctors.find((d) => d.doctor_id === form.doctor_id);
          return (
            <div className="space-y-4 text-sm" data-testid="opd-bill-preview">
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-xs text-slate-500">Patient</p><p className="font-medium">{picked?.name}</p></div>
                <div><p className="text-xs text-slate-500">UHID</p><p className="font-medium">{picked?.uhid}</p></div>
                <div><p className="text-xs text-slate-500">Doctor</p><p className="font-medium">Dr. {dr?.name}</p></div>
                <div><p className="text-xs text-slate-500">Consultation type</p><p className="font-medium capitalize">{form.visit_type.replace("_", " ")}</p></div>
              </div>
              <table className="w-full"><tbody>
                {opdLines().map((l, i) => (
                  <tr key={i} className="border-b border-slate-100"><td className="py-1.5">{l.description}</td><td className="py-1.5 text-right">{inr(l.rate * l.quantity)}</td></tr>
                ))}
              </tbody></table>
              <div className="space-y-1 border-t border-slate-200 pt-2">
                <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{inr(b.subtotal)}</span></div>
                <div className="flex justify-between text-slate-600"><span>GST / tax</span><span>{inr(b.tax)}</span></div>
                {b.discountAmount > 0 && <div className="flex justify-between text-slate-600"><span>Discount</span><span>-{inr(b.discountAmount)}</span></div>}
                <div className="flex justify-between font-semibold text-base pt-1"><span>Total</span><span data-testid="opd-preview-total">{inr(b.total)}</span></div>
                <div className="flex justify-between text-emerald-700"><span>Paid</span><span>{inr(b.paid)}</span></div>
                <div className="flex justify-between text-amber-700"><span>Due</span><span>{inr(b.due)}</span></div>
              </div>
              {formError && <p className="text-sm text-red-600" data-testid="opd-preview-error">{formError}</p>}
              <div className="flex gap-3 justify-end">
                <button type="button" className={ghostBtnCls} disabled={submitting || submissionAttempted.current}
                  onClick={() => setBillPreview(false)} data-testid="opd-preview-cancel">Cancel</button>
                <button type="button" className={primaryBtnCls} disabled={submitting}
                  onClick={confirmSave} data-testid="opd-preview-save-print">
                  {submitting ? "Processing..." : "Save & Print"}
                </button>
              </div>
            </div>
          );
        })()}
      </Modal>

      <PrintPreview open={Boolean(receipt)} paperSize="A4" title="OPD payment receipt"
        subtitle={receipt?.patient?.name} testid="opd-receipt-preview"
        onClose={() => { setReceipt(null); if (pendingRx) { setRxPreview(pendingRx); setPendingRx(null); } }}>
        <HospitalInvoiceDoc invoice={receipt?.invoice} patient={receipt?.patient} business={receipt?.business} />
      </PrintPreview>
    </div>
  );
}
